/** @format */

import {useState, useEffect, useContext} from 'react';
import SuperAgent from 'superagent';
import update from 'immutability-helper';
import {QuoteContext} from 'frontier-quoter';

export function useDesignsOrdersHandler({orderId, lastUpdate}) {
    const context = useContext(QuoteContext);
    const [state, setState] = useState({
        loading: true,
        designsOrders: [],
        totalQty: 0,
        designQty: 0,
        isPreviewModalActive: false
    });

    useEffect(() => {
        fetchDesignsOrders();
    }, [orderId, context.order.is_front_back, context.order.is_duplex]);

    useEffect(() => {
        handleRequestManuallyEnteredQuantities();
    }, [state.totalQty, state.designQty]);

    const fetchDesignsOrders = () => {
        let abortController = new AbortController();
        let updateTimeout;

        if (updateTimeout) {
            clearTimeout(updateTimeout);
        }

        updateTimeout = setTimeout(async () => {
            setState(prevState => ({...prevState, loading: true}));
            try {
                if (context.order.id) {
                    const {
                        body: {designsOrders}
                    } = await SuperAgent.get('/orders/quote-designs/' + context.order.id).set(
                        'Accept',
                        'application/json'
                    );

                    setState(prevState => ({
                        ...prevState,
                        designsOrders,
                        totalQty: context.order.total_qty || '',
                        designQty: context.order.design_qty || '',
                        loading: false,
                        isAutofilling: false
                    }));
                } else {
                    setState(prevState => ({
                        ...prevState,
                        designsOrders: [],
                        totalQty: '',
                        designQty: '',
                        loading: false,
                        isAutofilling: false
                    }));
                }
            } catch (e) {
                // Probably aborted
            }
        }, 500);
    };

    const handleDeleteDesign = async (designId, filestackId) => {
        setState(prevState => {
            const designIndex = prevState.designsOrders.findIndex(design => design.id === designId);
            const updatedState = update(prevState, {
                designsOrders: {$splice: [[designIndex, 1]]}
            });
            if (updatedState.designsOrders.length === 0) {
                handleRequestManuallyEnteredQuantities();
            }
            return updatedState;
        });
        context.handleUpdate({});
    };

    const handleDeleteAllDesigns = async () => {
        await SuperAgent.delete('/orders/delete-all-quoter-designs-orders/' + context.order.id).set(
            'accept',
            'application/json'
        );
        setState(prevState => ({...prevState, designsOrders: []}));
    };

    const handleUploadComplete = fileEntity => {
        setState(prevState =>
            update(prevState, {
                designsOrders: {$push: [fileEntity]},
                designQty: {$set: prevState.designsOrders.length + 1},
                totalQty: {
                    $set: prevState.designsOrders.reduce((total, dos) => {
                        total += dos.qty;
                        return total;
                    }, fileEntity.qty)
                }
            })
        );
        context.handleUpdate({});
    };

    const handleUploadCompleteAll = async fileEntities => {
        await SuperAgent.get('/orders/quote-sync-quoter-designs/' + context.order.id).set(
            'accept',
            'application/json'
        );
        fetchDesignsOrders();
    };

    const handleManuallyEnteredQuantities = (field, {target: {value}}) => {
        setState(prevState => update(prevState, {[field]: {$set: value}}));
    };

    const handleRequestManuallyEnteredQuantities = () => {
        let abortController = new AbortController();
        let updateTimeout;

        if (updateTimeout) {
            clearTimeout(updateTimeout);
        }

        updateTimeout = setTimeout(async () => {
            try {
                if (context.order.id) {
                    const endpoint = `/orders/quote-update-total-quantities/${context.order.id}`;
                    const response = await fetch(endpoint, {
                        signal: abortController.signal,
                        method: 'POST',
                        body: JSON.stringify({
                            total_qty: state.totalQty,
                            design_qty: state.designQty
                        }),
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });

                    const {code} = await response.json();
                    context.handleUpdate({});
                    if (code === 404) {
                        alert('Order ID not found. Please try again');
                    }
                }
            } catch (e) {
                // probably aborted
            }
        }, 500);
    };

    const handleAutoQty = qty => {
        setState(prevState => ({...prevState, isAutofilling: true}));
        const designsOrdersIds = state.designsOrders
            .filter(dos => dos.qty === 0)
            .map(dos => dos.id);

        SuperAgent.post('/orders/quote-update-all-designs-orders')
            .set('accept', 'application/json')
            .send({
                order_id: context.order.id,
                designs_orders_ids: designsOrdersIds,
                new_qty: qty
            })
            .then(() => {
                fetchDesignsOrders();
                context.handleUpdate({});
            });
    };

    return {
        ...state,
        fetchDesignsOrders,
        handleDeleteDesign,
        handleDeleteAllDesigns,
        handleUploadComplete,
        handleUploadCompleteAll,
        handleManuallyEnteredQuantities,
        handleAutoQty
    };
}
